import type { ImageProps } from 'next/image'
import type { FunctionComponent } from 'react'
import type { ImageResult } from '../data/ImageFragment'
import { WEB_URL } from '../utilities/webUrl'
import styles from './ImageBlock.module.sass'
import { ResponsiveImage } from './ResponsiveImage'
import { useArticle } from './contexts/ArticleContextProvider'
import { useTranslate } from './contexts/TranslationsContextProvider'

export type ImageBlockProps = ImageResult & Pick<ImageProps, 'sizes'>

export const ImageBlock: FunctionComponent<ImageBlockProps> = ({ id, url, width, height, alt, sizes }) => {
	const { articleUrl, articleTitle } = useArticle()
	const translate = useTranslate()

	const handleLinkedInShare = () => {
		if (!articleUrl) return

		// Generate the share URL with image metadata
		const params = new URLSearchParams({
			imageUrl: url,
			alt: alt || '',
			title: articleTitle || '',
		})

		// Include parameters directly in the share URL
		const shareUrl = `${WEB_URL}/api/share/${id}${articleUrl}?${params.toString()}`

		// URL encode the complete share URL for LinkedIn
		const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`

		// Open LinkedIn share dialog in new window
		window.open(linkedInUrl, '_blank', 'width=600,height=600')
	}

	return (
		<div className={styles.wrapper}>
			<div
				className={styles.imageWrapper}
				style={{
					'--ImageBlock-image-width': width,
				}}
			>
				<ResponsiveImage
					className={styles.image}
					src={url}
					width={width}
					height={height}
					alt={alt ?? ''}
					sizes={sizes}
					isBorderRadius
				/>
				{articleUrl && (
					<button
						type="button"
						className={styles.shareButton}
						onClick={handleLinkedInShare}
						aria-label={`${translate('article.shareOnLinkedIn')} ↗` || 'Sdílet na LinkedIn ↗'}
					>
						<svg width="46" height="46" viewBox="0 0 46 46" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path
								d="M23 0C10.3093 0 0 10.3093 0 23C0 35.6907 10.3093 46 23 46C35.6907 46 46 35.6907 46 23C46 10.3093 35.6907 0 23 0ZM23 2C34.6098 2 44 11.3902 44 23C44 34.6098 34.6098 44 23 44C11.3902 44 2 34.6098 2 23C2 11.3902 11.3902 2 23 2ZM14 10C12.355 10 11 11.355 11 13C11 14.645 12.355 16 14 16C15.645 16 17 14.645 17 13C17 11.355 15.645 10 14 10ZM14 12C14.5641 12 15 12.4359 15 13C15 13.5641 14.5641 14 14 14C13.4359 14 13 13.5641 13 13C13 12.4359 13.4359 12 14 12ZM12 17C11.7348 17 11.4805 17.1054 11.2929 17.2929C11.1054 17.4805 11 17.7348 11 18V33C11 33.2652 11.1054 33.5195 11.2929 33.7071C11.4805 33.8946 11.7348 34 12 34H16C16.2652 34 16.5195 33.8946 16.7071 33.7071C16.8946 33.5195 17 33.2652 17 33V18C17 17.7348 16.8946 17.4805 16.7071 17.2929C16.5195 17.1054 16.2652 17 16 17H12ZM20 17C19.7348 17 19.4805 17.1054 19.2929 17.2929C19.1054 17.4805 19 17.7348 19 18V33C19 33.2652 19.1054 33.5195 19.2929 33.7071C19.4805 33.8946 19.7348 34 20 34H24C24.2652 34 24.5195 33.8946 24.7071 33.7071C24.8946 33.5195 25 33.2652 25 33V25.5C25 24.1205 26.1205 23 27.5 23C28.8795 23 30 24.1205 30 25.5V28V33C30 33.2652 30.1054 33.5195 30.2929 33.7071C30.4805 33.8946 30.7348 34 31 34H35C35.2652 34 35.5195 33.8946 35.7071 33.7071C35.8946 33.5195 36 33.2652 36 33V24.5C36 20.3696 32.6304 17 28.5 17C27.2135 17 26.0597 17.4162 25 17.9902C24.9974 17.7267 24.8909 17.4749 24.7036 17.2895C24.5164 17.104 24.2635 17 24 17H20ZM13 19H15V32H13V19ZM21 19H23V19.8164C23 20.0125 23.0577 20.2043 23.1659 20.3679C23.2741 20.5315 23.428 20.6597 23.6084 20.7365C23.7889 20.8133 23.988 20.8354 24.1809 20.7999C24.3738 20.7645 24.552 20.6731 24.6934 20.5371C25.6842 19.5853 27.0167 19 28.5 19C31.5496 19 34 21.4504 34 24.5V32H32V28V25.5C32 23.0295 29.9705 21 27.5 21C25.0295 21 23 23.0295 23 25.5V32H21V19Z"
								fill="white"
							/>
						</svg>
					</button>
				)}
			</div>
			{alt && <div className={styles.alt}>{alt}</div>}
		</div>
	)
}
